import { observable, computed } from 'mobx'
import { once } from 'lodash-es'
import { Permissions } from '@/features/iam/permissions/Permissions'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Model, type ModelProps } from '@/infra/model'
import { User } from '@/features/iam/users/User'
import { unixEpoch } from '@/utils/date-util'
import { Id } from '@/infra/id'
import { generateAvatarFallback } from '@/utils/image-util'
import { type Role } from '@/features/iam/roles/Role'
import { type Skill } from '@/features/iam/skills/Skill'
import { type Membership } from '@/features/iam/workspaces/Membership'

export type MemberMetadata = Record<string, unknown>

export type MemberProps = ModelProps<Member>

export class Member extends Model<Member> {
  @observable accessor workspace = Workspace.none
  @observable accessor user = User.none
  @observable accessor internalId = Id.none
  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor displayName = ''
  @observable accessor email = ''
  @observable accessor picture = ''
  @observable accessor phone = ''
  @observable accessor memberMetadata: MemberMetadata = {}
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor lastSeen: Date | null = unixEpoch()
  @observable accessor lastLogin: Date | null = unixEpoch()

  @observable accessor inactive = false
  @observable accessor maxConcurrentProviderJobs = 0
  @observable accessor approvedTermsVersion = ''
  @observable accessor referrer = ''
  @observable accessor permissions = Permissions.empty()
  @observable accessor roles: Role[] = []
  @observable accessor skills: Skill[] = []

  /**
   * Default member.
   */
  static get none() {
    return lazyNone()
  }

  @computed
  get isCurrentUser() {
    return this.user.isCurrentUser
  }

  @computed
  get avatar() {
    return this.picture || this.avatarFallback
  }

  @computed
  get avatarFallback() {
    return generateAvatarFallback(
      this.displayName || this.email,
      this.workspace.branding.colors.primary,
    )
  }

  /**
   * Whether the member may list the workspace's clients. Advisors may in a
   * workspace with client assignment, where the server only returns the clients
   * assigned to them.
   */
  @computed
  get canListClients() {
    return (
      this.permissions.adminUserList ||
      this.permissions.adminClientUserList ||
      (this.permissions.clientAssignmentManage &&
        this.workspace.config.externalCustomer.clientAssignmentEnabled)
    )
  }

  /**
   * Whether the current member represents that of the input
   * IDs.
   */
  is(workspaceId: Id, userId: Id) {
    return this.workspace.id === workspaceId && this.user.id === userId
  }

  toMembership(): Membership {
    return {
      member: this,
      workspace: this.workspace,
    }
  }
}

const lazyNone = once(() =>
  new Member().set({
    givenName: 'Member',
    familyName: 'Member',
    displayName: 'Member',
  }),
)
